import React, { useState } from 'react';
import { Col, Label, Row } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useProcedures } from '../../containers/MedicalRecords/Hooks/useProcedures';
import { CustomSearchBox } from '../CustomControls/CustomSearchBox';
import { NullableDatePickerCombo } from '../CustomControls/DatePicker';
import { ExpandableContent } from '../CustomControls/ExpandableContent';
import { dateFormatter } from '../Utils/Formatter';
import { EntryItemsTable } from './EntryItemsTable';
import { Tooltip } from '../Utils/Tooltips';
import { Add } from '../Icons/Icons';

export const Procedures = (props) => {
    const { t } = useTranslation();

    const columns = [
        {
            dataField: 'procedure', text: t('medicalRecords.procedures.procedure'), formatter: function (_cell, row, rowindex) {
                return (

                    <Row key={`procedure-item-${rowindex}`} style={{ textAlign: 'left' }} className="item-data">
                        <Col md={8}>
                            <span className="item-text">{row.procedure}</span>
                            {
                                (row.from || row.to) &&
                                <div>
                                    {row.from && <span><span className="item-label">{t('medicalRecords.from')}:</span> <span className="item-text">{row.from}</span></span>}
                                    {row.from && row.to && <span>&nbsp;&nbsp;</span>}
                                    {row.to && <span><span className="item-label">{t('medicalRecords.to')}:</span> <span className="item-text">{row.to}</span></span>}
                                </div>
                            }
                        </Col>
                        <Col md={4} style={{ textAlign: 'right' }}>
                            <div><span className="item-label">{t('medicalRecords.itemDates.created')}:</span> <span className="item-text">{dateFormatter(row.created, "DD/MM/YY")}</span></div>
                            {
                                moment(row.modified).format("DD/MM/YY") !== moment(row.created).format("DD/MM/YY") &&
                                <div><span className="item-label">{t('medicalRecords.itemDates.modified')}:</span> <span className="item-text">{dateFormatter(row.modified, "DD/MM/YY")}</span></div>
                            }
                            {row.toYear && row.entryId == props.entryId &&
                                <div><Label bsStyle="danger">{t('medicalRecords.itemDates.closedHere')}</Label></div>
                            }
                        </Col>
                    </Row>
                )
            }
        },
    ];

    const customActions = function (row, props) {
        if (props.onAddProcedure && props.entryId != row.entryId)
            return <Tooltip tooltip={t("medicalRecords.procedures.addProcedure")} id={"tooltipadprocedure-" + row.id}>
                <button className="btn-table-actions" onClick={() => props.onAddProcedure(row)}>
                    <Add />
                </button>
            </Tooltip>
    }

    const expandedContent = (row) => (
        <div className="info">
            {row.reason && <div><span className="patient-label">{t('medicalRecords.procedures.reason')}:</span> <span className="patient-text">{row.reason}</span></div>}
            {row.notes && <div><span className="patient-label">{t('medicalRecords.procedures.notes')}:</span> <span className="patient-text">{row.notes}</span></div>}
        </div>
    )
    const rowClasses = (row) => { return row.logicalItemId && row.logicalItemId !== row.id ? 'item-modified' : ''; };

    return (
        <>
            {props.onSearchTermChange && <CustomSearchBox handleSearch={props.onSearchTermChange} placeholder={t('medicalRecords.searchByName')} />}
            <EntryItemsTable columns={columns} customActions={customActions} {...props} rowClasses={rowClasses} expandContent={expandedContent} />
        </>
    );
}

export const ProceduresOpenItems = (props) => {
    const { value, medicalRecordNumber, expandedAll, isDisabled, title } = props;
    const [procedures, query, totalSize, isLoading, handleTableChange] = useProcedures(medicalRecordNumber, null, true);
    const [closedItems, setClosedItems] = useState(value !== "" && value !== null ? value : []);
    const { t } = useTranslation();

    const handleChange = (item, name, value) => {
        let dateName = name.split(".")[1];
        var closedItem = closedItems.find(x => x.id === item.id);

        if (closedItem) { // Update
            closedItem.closed[dateName] = value;

            if (!closedItem.closed.toDay && !closedItem.closed.toMonth && !closedItem.closed.toYear) {
                const closedItemsCopy = closedItems.filter(x => x.id !== closedItem.id); // Remove
                setClosedItems(closedItemsCopy);
                props.onChange(props.name, closedItemsCopy);
            } else {
                let closedItemsCopy = JSON.parse(JSON.stringify(closedItems));
                var index = closedItemsCopy.findIndex(x => x.id === item.id);
                closedItemsCopy.splice(index, 1, closedItem); // Update. 
                setClosedItems(closedItemsCopy);
                props.onChange(props.name, closedItemsCopy);
            }
        }
        else {
            // New
            var joined = closedItems.concat({
                id: item.id,
                closed: { [dateName]: value },
                name: item.procedure
            });
            setClosedItems(joined);
            props.onChange(props.name, joined);
        }
    }

    const handleBlur = () => {
        if (props.onBlur)
            props.onBlur(props.name, true);
    };

    const columns = [
        { dataField: 'procedure', text: t('medicalRecords.procedures.procedure') },
        { dataField: 'from', text: t('medicalRecords.from') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                var closedItem = closedItems?.find(x => x.id === row.id);

                return (<span className="item-text">
                    <NullableDatePickerCombo
                        nameDay={`${rowindex}.toDay`}
                        nameMonth={`${rowindex}.toMonth`}
                        nameYear={`${rowindex}.toYear`}
                        onChange={(name, value) => handleChange(row, name, value)}
                        onBlur={handleBlur}
                        valueDay={closedItem ? closedItem.closed.toDay : row.toDay}
                        valueMonth={closedItem ? closedItem.closed.toMonth : row.toMonth}
                        valueYear={closedItem ? closedItem.closed.toYear : row.toYear}
                        disabled={isDisabled}
                    />
                </span>)
            }
        }
    ];

    const columnsClosed = [
        { dataField: 'name', text: t('medicalRecords.procedures.procedure') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                if (!row.closed) return "";

                return (
                    <span className="item-text">
                        <NullableDatePickerCombo
                            nameDay={`${rowindex}.toDay`}
                            nameMonth={`${rowindex}.toMonth`}
                            nameYear={`${rowindex}.toYear`}
                            onChange={(name, value) => handleChange(row, name, value)}
                            onBlur={handleBlur}
                            valueDay={row.closed.toDay}
                            valueMonth={row.closed.toMonth}
                            valueYear={row.closed.toYear}
                            disabled={isDisabled}
                        />
                    </span>
                )
            }
        }
    ];

    return (
        <>
            {
                value && value.length > 0 &&
                <ExpandableContent title={t("medicalRecords.closed")} expandedAll={expandedAll} content="adverseEvents">
                    <BootstrapTable
                        bordered={false}
                        classes="table-spaced table-spaced-small"
                        wrapperClasses="table-responsive"
                        striped
                        hover
                        keyField='id'
                        data={value ? value : []}
                        columns={columnsClosed}
                    />
                </ExpandableContent>
            }
            <ExpandableContent title={title} expandedAll={expandedAll} content="procedures">
                <BootstrapTable
                    bordered={false}
                    classes="table-spaced table-spaced-small"
                    wrapperClasses="table-responsive"
                    striped
                    hover
                    remote
                    keyField='id'
                    data={procedures ? procedures : []}
                    columns={columns}
                    pagination={query && query.pagination && paginationFactory({
                        page: query.pagination.page,
                        sizePerPage: query.pagination.sizePerPage,
                        totalSize: totalSize,
                        hideSizePerPage: true,
                        hidePageListOnlyOnePage: true
                    })}
                    onTableChange={handleTableChange}
                    noDataIndication={() => <>{t("commons.noData")}</>}
                    loading={isLoading}
                    overlay={overlayFactory({ spinner: true })}
                />
            </ExpandableContent>
        </>
    );
}